// src/components/NotificationStatus.tsx
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, ExternalLink, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorageString } from '@/hooks/use-local-storage';
import { AccountType } from '@/pages/auth/components/user-auth-form';
import { DeviceDetection } from '@/utils/deviceDetection';

export function NotificationStatus() {
  const navigate = useNavigate();
  const accountType = useLocalStorageString('accountType') as AccountType;
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState<'enabled' | 'denied'>('denied');
  const [shouldHide, setShouldHide] = useState(false);

  const getNotificationPath = () => {
    switch (accountType) {
      case AccountType.business:
      case AccountType.business_admin:
        return '/notifications'

      case AccountType.client:
      case AccountType.app_client:
        return '/client-portal/notifications'

      case AccountType.business_team_leader:
      case AccountType.team_leader:
      case AccountType.staff_team_leader:
        return '/team-leader/notifications'

      case AccountType.business_operations_managers:
      case AccountType.business_operations_manager:
      case AccountType.staff_operations_manager:
      case AccountType.operations_manager:
        return '/operations-manager/notifications'
      
      default:
        return '/notifications'
    }
  }

  useEffect(() => {
    // Check if we should hide on mobile/PWA
    setShouldHide(DeviceDetection.shouldHideNotifications());
    
    // Only show ONCE after user made decision
    const alreadyAsked = localStorage.getItem('notifications_asked');
    const notificationStatus = localStorage.getItem('notifications_status');
    
    if (alreadyAsked && notificationStatus && !DeviceDetection.shouldHideNotifications()) {
      setStatus(notificationStatus as 'enabled' | 'denied');
      setShow(true);
      
      // Hide after 35 seconds
      setTimeout(() => {
        setShow(false);
      }, 35000);
    }
  }, []);

  // Hide component for mobile/PWA
  if (shouldHide || !show) return null;

  if (status === 'enabled') {
    return (
      <Alert className="mb-4 border-green-200 bg-green-50">
        <Bell className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="font-medium">🔔 Notifications enabled!</p>
              <p className="text-sm mt-1">
                You'll receive real-time work updates. Want to refresh permissions? 
                Go to{' '}
                <button 
                  onClick={() => navigate(getNotificationPath())}
                  className="underline hover:no-underline font-medium"
                >
                  Notifications
                </button>
                {' '}and click "Refresh Permissions".
              </p>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-4 border-yellow-200 bg-yellow-50">
      <BellOff className="h-4 w-4 text-yellow-600" />
      <AlertDescription className="text-yellow-800">
        <div className="space-y-3">
          <div>
            <p className="font-medium">Notifications disabled</p>
            <p className="text-sm mt-1">
              You won't receive real-time updates. To enable notifications:
            </p>
          </div>
          
          <div className="text-sm space-y-2">
            <div className="flex items-start gap-2">
              <span className="font-medium text-yellow-900 min-w-[20px]">1.</span>
              <span>Click the lock icon 🔒 in your browser's address bar</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium text-yellow-900 min-w-[20px]">2.</span>
              <span>Change "Notifications" from "Block" to "Allow"</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium text-yellow-900 min-w-[20px]">3.</span>
              <span>Refresh the page to enable notifications</span>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(getNotificationPath())}
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              <Settings className="w-3 h-3 mr-1" />
              Notification Settings
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Refresh Page
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
